import { mediaRef } from '@/services/mediaAssets';

const reckonWayHeroImage = mediaRef('adventures/the-reckon-way-hero.png');

export const welcomeToReckonWayStory = {
  id: 'welcome-to-the-reckon-way',
  title: 'Welcome to The Reckon Way',
  slug: 'welcome-to-the-reckon-way',
  href: '/the-reckon-way/stories/welcome-to-the-reckon-way',
  excerpt: 'The destination is the backdrop. The people are the story. Discover why shared journeys have become such an important part of Reckon.',
  image: reckonWayHeroImage,
  imageAlt: 'The Reckon Way collection of journeys, traditions, and shared stories',
  contentType: 'story',
  publishedAt: '2026-07-17',
  featured: true,
  featuredPriority: 0,
  pinned: true,
  badge: 'Start Here',
  article: {
    title: 'Welcome to The Reckon Way',
    lede: 'The destination is the backdrop. The people are the story.',
    images: [
      {
        src: reckonWayHeroImage,
        alt: 'The Reckon Way collection of journeys, traditions, and shared stories',
        wide: true,
      },
    ],
    sections: [
      {
        heading: 'Where Great Journeys Build Great Relationships',
        paragraphs: [
          'Some of life\'s most meaningful moments aren\'t found on an itinerary.',
          'They happen in the conversations after dinner.',
          'On the road between destinations.',
          'Around a campfire.',
          'During an unexpected challenge.',
          'Or in the quiet moments when you stop long enough to appreciate the people sharing the journey with you.',
        ],
      },
      {
        heading: 'The People Are the Story',
        paragraphs: [
          'That\'s what The Reckon Way is all about.',
          'It\'s a collection of stories about adventure, curiosity, leadership, family, and the experiences that bring people together.',
          'Some stories take us halfway around the world.',
          'Others happen much closer to home.',
          'Every one of them reminds us that while destinations may inspire us, it\'s the people we share them with who leave the greatest impact.',
        ],
        emphasis: 'Explore. Learn. Connect.',
        links: [
          {
            label: 'Read the Prologue: Why We Go',
            route: '/the-reckon-way/stories/why-we-go',
            primary: true,
          },
        ],
      },
    ],
  },
};

export const whyWeGoStory = {
  id: 'why-we-go',
  title: 'Why We Go',
  slug: 'why-we-go',
  href: '/the-reckon-way/stories/why-we-go',
  excerpt: 'The destination matters. But it isn\'t the most important part. The people are.',
  image: reckonWayHeroImage,
  imageAlt: 'The Reckon Way collection of journeys, traditions, and shared stories',
  contentType: 'story',
  publishedAt: '2026-07-19',
  badge: 'Prologue',
  kicker: 'Welcome · Prologue',
  article: {
    title: 'Why We Go',
    lede: 'People often ask why we invest so much in experiences.',
    sections: [
      {
        heading: 'The Question',
        paragraphs: [
          'It\'s a fair question.',
          'Experiences take time.',
          'They require planning.',
          'They\'re expensive.',
          'And when you look at them on a spreadsheet, it\'s difficult to measure their return.',
          'You can\'t easily quantify trust.',
          'Or friendship.',
          'Or the confidence someone gains by trying something they never thought they could do.',
          'You can\'t measure the conversation that happens over dinner after a day of shared challenges.',
          'Or the relationship that begins because two people happened to sit beside each other at breakfast.',
          'But those moments matter.',
          'In many ways, they matter more than the meetings we\'ll ever schedule.',
        ],
      },
      {
        heading: 'Shared Experiences',
        paragraphs: [
          'Over the years, I\'ve come to believe that organizations aren\'t built through presentations.',
          'They\'re built through experiences.',
          'Through challenges that are faced together.',
          'Through laughter.',
          'Through unexpected problems.',
          'Through stories that people continue telling long after the event itself has ended.',
          'The strongest teams aren\'t created because people work together.',
          'They\'re created because people know one another.',
          'Not just as coworkers.',
          'As parents.',
          'As spouses.',
          'As friends.',
          'As people.',
        ],
      },
      {
        heading: 'Why Families Matter',
        paragraphs: [
          'That\'s why families matter.',
          'When you invite families into the experience, something changes.',
          'The conversations become different.',
          'The laughter becomes louder.',
          'The memories become shared across generations instead of confined to an office.',
          'Children don\'t remember strategy meetings.',
          'They remember adventures.',
          'Years later, so do the adults.',
        ],
      },
      {
        heading: 'The Story of One Week',
        paragraphs: [
          'This isn\'t a guide to building culture.',
          'It isn\'t a travel guide.',
          'It\'s simply the story of one week that reminded me of something I probably knew all along.',
          'People connect through shared experiences.',
          'The destination matters.',
          'But it isn\'t the most important part.',
          'The people are.',
        ],
      },
      {
        heading: 'The Space Between',
        paragraphs: [
          'Looking back, I don\'t remember our retreat as a schedule of activities.',
          'I remember moments.',
          'Moments that made us laugh.',
          'Moments that challenged us.',
          'Moments that reminded us to slow down.',
          'Moments that quietly changed the way we saw one another.',
          'Most of those moments were never part of the itinerary.',
          'They happened in the space between it.',
        ],
      },
      {
        heading: 'Come With Us',
        paragraphs: [
          'That\'s what I hope you find in these stories.',
          'Not a list of places to visit.',
          'But a reminder that the most meaningful experiences are rarely about where you go.',
          'They\'re about who you go with.',
          'If these stories inspire you to take one more trip with your family, plan an experience your team will still be talking about years from now, or simply slow down long enough to appreciate the people you\'re sharing the journey with...',
          'Then they\'ve done exactly what I hoped they would.',
          'Come with us.',
          'Our story begins at Gate A24.',
        ],
        links: [
          {
            label: 'Continue to Gate A24',
            route: '/the-reckon-way/iceland-corporate-retreat-2026/stories/gate-a24',
            primary: true,
          },
        ],
      },
    ],
  },
};

export const cultureStories = [welcomeToReckonWayStory, whyWeGoStory];

export const cultureStoriesBySlug = cultureStories.reduce((lookup, story) => ({
  ...lookup,
  [story.slug]: story,
}), {});

export default cultureStories;
